import { useState } from 'react';

import { Box } from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { ActionsPopper } from 'common/components/ActionsPopper';
import { setCurrentEntity, setCurrentEntityType, setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { Roles } from 'common/constants/roles';
import { useAuthContext } from 'common/contexts/AuthContext';
import { IdentityOemMapping } from 'common/types/Entities';
import { DrawerEntitiesNames,Operations } from 'common/types/TypeDefs';
import { disableIfMissingAnyRole } from 'common/utils/DisableButtonEvaluator';

import { UserProfileIdentititesCard } from './UserProfileIdentitiesCard';
import { UserProfileIdentitiesDeleteDialog } from './UserProfileIdentitiesDeleteDialog';

type UserProfileIndetitiesListProps = {
    data: IdentityOemMapping[];
};

const LIST_WRAPPER_INLINE_STYLES = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    padding: '0 24px'
};

export function UserProfileIndetitiesList({
    data
}: Readonly<UserProfileIndetitiesListProps> ) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editingRow, setEditingRow] = useState<IdentityOemMapping | null>(null);
    const dispatch = useAppDispatch();
    const { currentUserRoles } = useAuthContext();
    const allowedDeleteRoles = [Roles.Admin, Roles.UserManager];
    const shouldDisableDeleteButton = disableIfMissingAnyRole(currentUserRoles, allowedDeleteRoles);

    const handleOpenActionsMenu = (row: IdentityOemMapping) => (event: React.MouseEvent<HTMLElement>) => {
        if (row.subject === editingRow?.subject && anchorEl) {
            handleClosePopper();
        } else {
            setEditingRow(row);
            setAnchorEl(event.currentTarget);
        }
        event.stopPropagation();
    };

    const handleClosePopper = () => {
        setEditingRow(null);
        setAnchorEl(null);
    };

    const handleDeleteButtonClick = () => {
        dispatch(setCurrentEntity(editingRow));
        dispatch(setCurrentEntityType(DrawerEntitiesNames.UserIdentities));
        dispatch(setDialogOpen(true));
        setAnchorEl(null);
    };

    return (
        <Box sx={LIST_WRAPPER_INLINE_STYLES}>
            {
                data.map(row => {
                    const { oemId, subject } = row;
                    return (
                        <UserProfileIdentititesCard
                            key={`${oemId}-${subject}`}
                            userProfileInfo={row}
                            onActionMenuClick={handleOpenActionsMenu(row)}
                        />
                    );
                })
            }
            <UserProfileIdentitiesDeleteDialog
                editingRow={editingRow}
            />
            <ActionsPopper
                anchorEl={anchorEl}
                handleClosePopper={handleClosePopper}
                actions={[
                    {
                        operation: Operations.Delete,
                        handleOperation: handleDeleteButtonClick,
                        disabled: shouldDisableDeleteButton
                    }
                ]}
            />
        </Box>
    );
}
